/**
 * Detalhe da reserva — a tela onde a máquina de estados opera (Fase 4).
 *
 * ---------------------------------------------------------------------------
 * OS BOTÕES VÊM DA MÁQUINA, NÃO DE `if`s DA TELA
 * ---------------------------------------------------------------------------
 * As ações disponíveis saem de `transicoesPossiveis(status, 'EMPRESA')` — a
 * MESMA lista fechada de `@locaveri/shared` que a API valida. As transições com
 * fluxo próprio (vistoria, fechamento, alocação, aprovação, prorrogação) são
 * removidas do menu genérico e ganham o seu bloco; o que sobra vira botão com
 * dialog de motivo quando a máquina o exige.
 *
 * A TIMELINE é a trilha append-only como veio do banco: quem fez o quê, quando
 * e por quê. Ela não é reconstruída na tela — é exibida.
 */

import {
  ROTULO_DO_DOCUMENTO_SOLICITAVEL,
  ROTULO_DO_FATOR,
  ROTULO_DO_STATUS_DE_CONTRATO,
  ROTULO_DO_STATUS_DE_DOCUMENTO,
  ROTULO_DO_STATUS_DE_MULTA,
  ROTULO_DO_TIPO_DE_SINISTRO,
  exigeMotivo,
  transicoesPossiveis,
  type Transicao,
} from '@locaveri/shared'
import { Link, useParams } from '@tanstack/react-router'
import {
  ArrowLeft,
  Car,
  ClipboardCheck,
  FileSignature,
  FileText,
  LoaderCircle,
  ReceiptText,
  ScanFace,
  ShieldCheck,
  Timer,
} from 'lucide-react'
import { type ReactElement, useState } from 'react'
import { toast } from 'sonner'

import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { BadgeDeRisco, BadgeDeStatusDeReserva } from '@/features/reservas/badges'
import {
  useAcaoDeContrato,
  useAlocar,
  useAnexarDocumento,
  useAprovar,
  useAvaliarDocumento,
  useBaixarContrato,
  useCandidatos,
  useContratos,
  useDesfazerAlocacao,
  useDocumentosDaReserva,
  useFechar,
  useGerarContrato,
  useMultasDaReserva,
  useProrrogar,
  useRecusar,
  useReserva,
  useSimularFechamento,
  useSinistrosDaReserva,
  useTransicionar,
  type CandidatoLido,
  type DocumentoDaReserva,
  type FechamentoLido,
  type ReservaDetalhe,
} from '@/features/reservas/api'
import { useAnaliseDaReserva } from '@/features/analise-cadastral/api'
import { QuadroDaAnalise } from '@/features/analise-cadastral/quadro-da-analise'
import { useUrlDeLeitura } from '@/features/arquivos/url-de-leitura'
import { CartaoDePagamentos } from '@/features/pagamentos/cartao-de-pagamentos'
import { useMotivosDeCancelamento } from '@/features/pesquisa/api'
import { errorMessage } from '@/lib/api'
import { formatarData, formatarDataHora } from '@/lib/datas'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { temPermissao } from '@/lib/permissoes'

// ---------------------------------------------------------------------------
// A página
// ---------------------------------------------------------------------------

export function ReservaPage(): ReactElement {
  const reservaId = useParams({ strict: false }).reservaId ?? null
  const consulta = useReserva(reservaId)

  if (reservaId === null) {
    return <ErrorState titulo="Reserva não informada" erro={null} />
  }
  if (consulta.isPending) {
    return (
      <div className="mx-auto w-full max-w-5xl p-6">
        <LoadingState rotulo="Carregando a reserva…" />
      </div>
    )
  }
  if (consulta.isError) {
    return (
      <div className="mx-auto w-full max-w-5xl p-6">
        <ErrorState
          titulo="Não foi possível carregar a reserva"
          erro={consulta.error}
          aoTentarNovamente={() => {
            void consulta.refetch()
          }}
        />
      </div>
    )
  }

  const reserva = consulta.data
  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <Link
            to="/reservas"
            className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-foreground"
          >
            <ArrowLeft className="size-4" /> Reservas
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">Reserva nº {reserva.numero}</h1>
            <BadgeDeStatusDeReserva status={reserva.status} />
            {reserva.risco === null ? null : (
              <BadgeDeRisco score={reserva.risco.score} nivel={reserva.risco.nivel} />
            )}
          </div>
          <p className="text-sm text-fg-secondary">
            {reserva.conta.nome} · {reserva.categoria.nome} · {formatarDataHora(reserva.retirada)} →{' '}
            {formatarDataHora(reserva.devolucao)}
          </p>
        </div>
        <AcoesDaMaquina reserva={reserva} />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <CartaoFinanceiro reserva={reserva} />
          <CartaoDePagamentos reserva={reserva} />
          <CartaoDeAlocacao reserva={reserva} />
          <CartaoDeContratos reserva={reserva} />
          <CartaoDeFechamento reserva={reserva} />
        </div>
        <div className="space-y-6">
          <CartaoDeCondutores reserva={reserva} />
          <CartaoDaAnaliseCadastral reserva={reserva} />
          <CartaoDeDocumentos reserva={reserva} />
          <Timeline reserva={reserva} />
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Ações da máquina (o menu genérico)
// ---------------------------------------------------------------------------

/** As ações que têm bloco/fluxo próprio nesta tela e saem do menu genérico. */
const ACOES_COM_FLUXO_PROPRIO = new Set([
  'APROVAR',
  'RECUSAR',
  'ALOCAR_VEICULO',
  'DESFAZER_ALOCACAO',
  'REGISTRAR_RETIRADA',
  'FINALIZAR',
  'PRORROGAR',
  'TROCAR_VEICULO',
])

function AcoesDaMaquina({ reserva }: { readonly reserva: ReservaDetalhe }): ReactElement {
  const transicionar = useTransicionar(reserva.id)
  const aprovar = useAprovar(reserva.id)
  const recusar = useRecusar(reserva.id)
  const prorrogar = useProrrogar(reserva.id)
  const [pedindoMotivo, setPedindoMotivo] = useState<Transicao | null>(null)
  /** O item do catálogo escolhido. Vazio = só texto livre. */
  const [motivoCatalogado, setMotivoCatalogado] = useState('')
  // `EMPRESA`: o painel é a superfície da equipe, e `AMBOS` já vem incluso.
  const motivos = useMotivosDeCancelamento({ origem: 'EMPRESA' })
  const [motivo, setMotivo] = useState('')
  const [prorrogando, setProrrogando] = useState(false)
  const [novaDevolucao, setNovaDevolucao] = useState('')
  const [motivoDaProrrogacao, setMotivoDaProrrogacao] = useState('')

  const podeAtualizar = temPermissao('reservation.update')
  const podeAprovar = temPermissao('reservation.approve')

  const transicoes = transicoesPossiveis(reserva.status, 'EMPRESA').filter(
    (transicao) => !ACOES_COM_FLUXO_PROPRIO.has(transicao.acao),
  )
  const podeProrrogar = transicoesPossiveis(reserva.status, 'EMPRESA').some(
    (transicao) => transicao.acao === 'PRORROGAR',
  )

  function executar(transicao: Transicao, motivoDado?: string, motivoDoCatalogo?: string): void {
    transicionar.mutate(
      {
        para: transicao.para,
        ...(motivoDado === undefined || motivoDado === '' ? {} : { motivo: motivoDado }),
        ...(motivoDoCatalogo === undefined || motivoDoCatalogo === ''
          ? {}
          : { motivoId: motivoDoCatalogo }),
      },
      {
        onSuccess: () => {
          toast.success('Feito.')
          setPedindoMotivo(null)
          setMotivo('')
        },
        onError: (erro) => {
          toast.error(errorMessage(erro))
        },
      },
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {reserva.status === 'AGUARDANDO_APROVACAO' && podeAprovar ? (
        <>
          <Button
            onClick={() => {
              aprovar.mutate(undefined, {
                onSuccess: () => {
                  toast.success('Reserva aprovada.')
                },
                onError: (erro) => {
                  toast.error(errorMessage(erro))
                },
              })
            }}
            disabled={aprovar.isPending}
          >
            <ShieldCheck /> Aprovar
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setPedindoMotivo({
                de: reserva.status,
                para: 'RECUSADA',
                atores: ['EMPRESA'],
                acao: 'RECUSAR',
                descricao: '',
                exigeMotivo: true,
              })
            }}
          >
            Recusar
          </Button>
        </>
      ) : null}

      {podeAtualizar
        ? transicoes.map((transicao) => (
            <Button
              key={transicao.acao}
              variant="outline"
              disabled={transicionar.isPending}
              title={transicao.descricao}
              onClick={() => {
                if (exigeMotivo(transicao.de, transicao.para, 'EMPRESA')) {
                  setPedindoMotivo(transicao)
                } else {
                  executar(transicao)
                }
              }}
            >
              {rotuloDaAcao(transicao.acao)}
            </Button>
          ))
        : null}

      {podeAtualizar && podeProrrogar ? (
        <Button
          variant="outline"
          onClick={() => {
            setProrrogando(true)
          }}
        >
          <Timer /> Prorrogar
        </Button>
      ) : null}

      {/* Motivo obrigatório: a máquina exige, o dialog colhe. */}
      <Dialog
        open={pedindoMotivo !== null}
        onOpenChange={(aberto) => {
          if (!aberto) {
            setPedindoMotivo(null)
            setMotivoCatalogado('')
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{rotuloDaAcao(pedindoMotivo?.acao ?? '')}</DialogTitle>
            <DialogDescription>
              Esta ação mexe em dinheiro ou em relacionamento — o motivo vai para a trilha e é
              obrigatório.
            </DialogDescription>
          </DialogHeader>
          {/* ⚠️ O CATÁLOGO VEM ANTES DO TEXTO LIVRE, e é ele que agrega.
              "achou caro", "carro caro", "preço" e "tava caro" são a MESMA
              resposta e quatro linhas distintas — ninguém agrupa isso à mão três
              meses depois. O texto livre continua obrigatório ao lado, porque
              nenhuma lista cobre tudo, e é dele que sai o item novo.

              A lista é a de ORIGEM `EMPRESA`: o painel é a superfície da equipe,
              e a API recusa (422) um motivo do cliente registrado por ela. */}
          {motivos.data !== undefined && motivos.data.itens.length > 0 && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="motivo-catalogado">Motivo</Label>
              <Select
                id="motivo-catalogado"
                value={motivoCatalogado}
                onChange={(evento) => {
                  setMotivoCatalogado(evento.target.value)
                }}
              >
                <option value="">Sem motivo do catálogo</option>
                {motivos.data.itens.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.rotulo}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <Textarea
            value={motivo}
            onChange={(evento) => {
              setMotivo(evento.target.value)
            }}
            placeholder="Descreva o motivo (mínimo de 5 caracteres)…"
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setPedindoMotivo(null)
              }}
            >
              Cancelar
            </Button>
            <Button
              disabled={motivo.trim().length < 5 || transicionar.isPending || recusar.isPending}
              onClick={() => {
                if (pedindoMotivo === null) {
                  return
                }
                if (pedindoMotivo.acao === 'RECUSAR') {
                  recusar.mutate(
                    { motivo: motivo.trim() },
                    {
                      onSuccess: () => {
                        toast.success('Reserva recusada, com o motivo na trilha.')
                        setPedindoMotivo(null)
                        setMotivo('')
                        setMotivoCatalogado('')
                      },
                      onError: (erro) => {
                        toast.error(errorMessage(erro))
                      },
                    },
                  )
                } else {
                  executar(pedindoMotivo, motivo.trim(), motivoCatalogado)
                }
              }}
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prorrogação: nova devolução + motivo, no fluxo próprio da máquina. */}
      <Dialog
        open={prorrogando}
        onOpenChange={(aberto) => {
          if (!aberto) {
            setProrrogando(false)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Prorrogar a locação</DialogTitle>
            <DialogDescription>
              As diárias adicionais entram no contratado pela diária congelada (
              {formatarMoedaDoWire(reserva.diariaBase)}). A agenda do veículo estica junto — se a
              próxima locação já tomou o período, a prorrogação é recusada.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              type="datetime-local"
              value={novaDevolucao}
              onChange={(evento) => {
                setNovaDevolucao(evento.target.value)
              }}
            />
            <Textarea
              value={motivoDaProrrogacao}
              onChange={(evento) => {
                setMotivoDaProrrogacao(evento.target.value)
              }}
              placeholder="Motivo da prorrogação (obrigatório)…"
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setProrrogando(false)
              }}
            >
              Cancelar
            </Button>
            <Button
              disabled={
                novaDevolucao === '' || motivoDaProrrogacao.trim().length < 5 || prorrogar.isPending
              }
              onClick={() => {
                const instante = new Date(novaDevolucao)
                if (Number.isNaN(instante.getTime())) {
                  toast.error('Informe a nova devolução.')
                  return
                }
                prorrogar.mutate(
                  { novaDevolucao: instante.toISOString(), motivo: motivoDaProrrogacao.trim() },
                  {
                    onSuccess: () => {
                      toast.success('Locação prorrogada.')
                      setProrrogando(false)
                      setNovaDevolucao('')
                      setMotivoDaProrrogacao('')
                    },
                    onError: (erro) => {
                      toast.error(errorMessage(erro))
                    },
                  },
                )
              }}
            >
              Prorrogar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** `ENVIAR_PARA_PAGAMENTO` -> "Enviar para pagamento". O verbo já é a UX. */
function rotuloDaAcao(acao: string): string {
  const texto = acao.toLowerCase().replaceAll('_', ' ')
  return texto.charAt(0).toUpperCase() + texto.slice(1)
}

// ---------------------------------------------------------------------------
// Cartões
// ---------------------------------------------------------------------------

function CartaoFinanceiro({ reserva }: { readonly reserva: ReservaDetalhe }): ReactElement {
  const linhas: readonly (readonly [string, string])[] = [
    ['Diária congelada', formatarMoedaDoWire(reserva.diariaBase)],
    ['Diárias', String(reserva.diarias)],
    ['Total', formatarMoedaDoWire(reserva.total)],
    ['Caução', formatarMoedaDoWire(reserva.caucao)],
    [
      'Franquia de km',
      reserva.kmDiarioIncluso === 0
        ? 'Quilometragem livre'
        : `${String(reserva.kmDiarioIncluso)} km/diária · excedente ${formatarMoedaDoWire(reserva.valorDoKmExcedente)}/km`,
    ],
    ['Tanque cheio', formatarMoedaDoWire(reserva.valorDoTanqueCheio)],
    [
      'Proteção',
      reserva.temProtecao
        ? reserva.franquiaDaProtecao === null
          ? 'Contratada — cobertura total'
          : `Contratada — franquia ${formatarMoedaDoWire(reserva.franquiaDaProtecao)}`
        : 'Sem proteção',
    ],
    ['Tolerância de devolução', `${String(reserva.toleranciaEmMinutos)} min`],
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ReceiptText className="size-4" /> Snapshot financeiro
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
          {linhas.map(([rotulo, valor]) => (
            <div key={rotulo} className="flex items-baseline justify-between gap-4">
              <dt className="text-sm text-fg-secondary">{rotulo}</dt>
              <dd className="text-sm font-medium tabular-nums">{valor}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-4 text-xs text-fg-muted">
          Congelado na criação da reserva — mudar a tabela de preço amanhã não muda esta conta, e o
          fechamento lê exatamente estes números.
        </p>
      </CardContent>
    </Card>
  )
}

function CartaoDeCondutores({ reserva }: { readonly reserva: ReservaDetalhe }): ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Condutores</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {reserva.condutores.map((condutor) => (
          <div key={condutor.id} className="rounded-md border border-border p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{condutor.nome}</span>
              {condutor.principal ? <Badge variant="outline">Principal</Badge> : null}
            </div>
            <p className="mt-1 text-xs text-fg-secondary">
              CNH {condutor.cnhNumero} ({condutor.cnhCategoria}) · válida até{' '}
              {formatarData(condutor.cnhValidade)}
            </p>
          </div>
        ))}
        {reserva.risco !== null && reserva.risco.alertas.length > 0 ? (
          <div className="rounded-md border border-danger-fg/40 bg-danger/10 p-3 text-sm text-danger-fg">
            <p className="font-medium">Alertas de risco</p>
            <ul className="mt-1 list-inside list-disc text-xs">
              {reserva.risco.alertas.map((alerta) => (
                <li key={alerta}>{alerta}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Análise cadastral (Mira Score)
// ---------------------------------------------------------------------------

/**
 * O mesmo quadro da fila. Só para quem lê dado de crédito
 * (`credit_analysis.read`) — o detalhe da reserva vai a papéis que não leem.
 */
function CartaoDaAnaliseCadastral({
  reserva,
}: {
  readonly reserva: ReservaDetalhe
}): ReactElement | null {
  const analise = useAnaliseDaReserva(reserva.id)
  if (analise.data === undefined) {
    return null
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ScanFace className="size-4" /> Análise do cliente
        </CardTitle>
      </CardHeader>
      <CardContent>
        <QuadroDaAnalise analise={analise.data} reservaId={reserva.id} />
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Documentos do cliente (Gap 2)
// ---------------------------------------------------------------------------

const VARIANTE_DO_STATUS_DE_DOCUMENTO = {
  SOLICITADO: 'warning',
  ENVIADO: 'info',
  APROVADO: 'success',
  REJEITADO: 'danger',
} as const

function CartaoDeDocumentos({
  reserva,
}: {
  readonly reserva: ReservaDetalhe
}): ReactElement | null {
  const documentos = useDocumentosDaReserva(reserva.id)

  // Sem pedido nenhum, o cartão não ocupa a coluna — quem pede é a fila.
  if (documentos.isPending || documentos.isError || documentos.data.itens.length === 0) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="size-4" /> Documentos do cliente
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {documentos.data.itens.map((documento) => (
          <LinhaDeDocumento key={documento.id} reservaId={reserva.id} documento={documento} />
        ))}
      </CardContent>
    </Card>
  )
}

function LinhaDeDocumento({
  reservaId,
  documento,
}: {
  readonly reservaId: string
  readonly documento: DocumentoDaReserva
}): ReactElement {
  const anexar = useAnexarDocumento(reservaId)
  const avaliar = useAvaliarDocumento(reservaId)
  const [rejeitando, setRejeitando] = useState(false)
  const [observacao, setObservacao] = useState('')
  const leitura = useUrlDeLeitura(documento.arquivoChave)

  const podeAnexar =
    temPermissao('reservation.update') &&
    (documento.status === 'SOLICITADO' || documento.status === 'REJEITADO')
  const podeAvaliar = temPermissao('reservation.approve') && documento.status === 'ENVIADO'

  function enviarArquivo(arquivo: File | undefined): void {
    if (arquivo === undefined) {
      return
    }
    anexar.mutate(
      { documentoId: documento.id, arquivo },
      {
        onSuccess: () => {
          toast.success('Documento anexado — aguardando avaliação.')
        },
        onError: (erro) => {
          toast.error(errorMessage(erro))
        },
      },
    )
  }

  function decidir(resultado: 'APROVADO' | 'REJEITADO'): void {
    avaliar.mutate(
      {
        documentoId: documento.id,
        resultado,
        ...(resultado === 'REJEITADO' ? { observacao: observacao.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast.success(resultado === 'APROVADO' ? 'Documento aprovado.' : 'Documento rejeitado.')
          setRejeitando(false)
          setObservacao('')
        },
        onError: (erro) => {
          toast.error(errorMessage(erro))
        },
      },
    )
  }

  return (
    <div className="space-y-2 rounded-md border border-border p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{ROTULO_DO_DOCUMENTO_SOLICITAVEL[documento.tipo]}</span>
        <Badge variant={VARIANTE_DO_STATUS_DE_DOCUMENTO[documento.status]}>
          {ROTULO_DO_STATUS_DE_DOCUMENTO[documento.status]}
        </Badge>
      </div>
      <p className="text-xs text-fg-secondary">{documento.instrucoes}</p>
      {documento.observacao === null ? null : (
        <p className="text-xs text-danger-fg">Avaliação: {documento.observacao}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {documento.arquivoChave !== null && leitura.data !== undefined ? (
          <a
            href={leitura.data.urlDeLeitura}
            target="_blank"
            rel="noreferrer noopener"
            className="text-xs text-brand-700 underline-offset-4 hover:underline dark:text-brand-500"
          >
            Ver arquivo enviado ↗
          </a>
        ) : null}
        {podeAnexar ? (
          <label className="inline-flex cursor-pointer items-center gap-1 text-xs text-fg-secondary hover:text-foreground">
            <input
              type="file"
              className="hidden"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              disabled={anexar.isPending}
              onChange={(evento) => {
                enviarArquivo(evento.target.files?.[0])
                evento.target.value = ''
              }}
            />
            {anexar.isPending ? (
              <LoaderCircle className="size-3 animate-spin" />
            ) : (
              <FileText className="size-3" />
            )}
            {documento.status === 'REJEITADO' ? 'Reenviar arquivo…' : 'Anexar arquivo…'}
          </label>
        ) : null}
        {podeAvaliar ? (
          <>
            <Button
              size="sm"
              variant="outline"
              disabled={avaliar.isPending}
              onClick={() => {
                decidir('APROVADO')
              }}
            >
              Aprovar
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={avaliar.isPending}
              onClick={() => {
                setRejeitando(!rejeitando)
              }}
            >
              Rejeitar…
            </Button>
          </>
        ) : null}
      </div>

      {rejeitando ? (
        <div className="space-y-2">
          <Textarea
            placeholder="Diga ao cliente por que o documento foi rejeitado (obrigatório)"
            value={observacao}
            onChange={(evento) => {
              setObservacao(evento.target.value)
            }}
          />
          <Button
            size="sm"
            variant="destructive"
            disabled={avaliar.isPending || observacao.trim().length < 5}
            onClick={() => {
              decidir('REJEITADO')
            }}
          >
            Confirmar rejeição
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function Timeline({ reserva }: { readonly reserva: ReservaDetalhe }): ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="space-y-4 border-l border-border pl-4">
          {[...reserva.timeline].reverse().map((passo, indice) => (
            <li key={`${passo.criadoEm}-${String(indice)}`} className="relative">
              <span className="absolute -left-[21px] top-1.5 size-2 rounded-full bg-border-strong" />
              <div className="flex flex-wrap items-center gap-2">
                <BadgeDeStatusDeReserva status={passo.status} />
                <span className="text-xs text-fg-muted">{formatarDataHora(passo.criadoEm)}</span>
              </div>
              <p className="mt-1 text-xs text-fg-secondary">
                {rotuloDaAcao(passo.acao)} · {passo.autor === null ? 'sistema' : passo.autor.nome}
                {passo.motivo === null ? '' : ` — “${passo.motivo}”`}
              </p>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Alocação
// ---------------------------------------------------------------------------

function CartaoDeAlocacao({ reserva }: { readonly reserva: ReservaDetalhe }): ReactElement {
  const [aberto, setAberto] = useState(false)
  const [desfazendo, setDesfazendo] = useState(false)
  const [motivo, setMotivo] = useState('')
  const desfazer = useDesfazerAlocacao(reserva.id)

  const ativa = reserva.alocacoes.find((alocacao) => alocacao.ativa)
  const podeAlocar = temPermissao('allocation.create') && reserva.status === 'APROVADA'
  // RETIRADA_PENDENTE também desfaz — a API cancela o contrato junto
  // (distrato quando assinado) e a reserva volta para APROVADA.
  const podeDesfazer =
    temPermissao('allocation.update') &&
    (reserva.status === 'VEICULO_ALOCADO' || reserva.status === 'RETIRADA_PENDENTE')
  const podeTrocar = temPermissao('allocation.update') && reserva.status === 'EM_ANDAMENTO'

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Car className="size-4" /> Alocação
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {ativa === undefined ? (
          <p className="text-sm text-fg-secondary">
            Nenhum veículo atribuído.{' '}
            {reserva.status === 'APROVADA'
              ? 'A reserva está aprovada e aguarda alocação.'
              : 'A alocação acontece depois da aprovação.'}
          </p>
        ) : (
          <div className="rounded-md border border-border p-3">
            <p className="font-medium">
              {ativa.veiculo.placa} · {ativa.veiculo.modelo}
            </p>
            <p className="mt-1 text-xs text-fg-secondary">
              {ativa.automatica
                ? `Escolhido pelo algoritmo${ativa.score === null ? '' : ` — score ${String(ativa.score)}`}`
                : `Escolha manual — “${ativa.justificativa ?? ''}”`}
            </p>
          </div>
        )}

        {reserva.alocacoes.some((alocacao) => !alocacao.ativa) ? (
          <div className="text-xs text-fg-muted">
            {reserva.alocacoes
              .filter((alocacao) => !alocacao.ativa)
              .map((alocacao) => (
                <p key={alocacao.id}>
                  {alocacao.veiculo.placa} — desfeita
                  {alocacao.motivoDoDesfazimento === null
                    ? ''
                    : `: “${alocacao.motivoDoDesfazimento}”`}
                </p>
              ))}
          </div>
        ) : null}

        <div className="flex gap-2">
          {podeAlocar ? (
            <Button
              onClick={() => {
                setAberto(true)
              }}
            >
              Alocar veículo
            </Button>
          ) : null}
          {podeDesfazer ? (
            <Button
              variant="outline"
              onClick={() => {
                setDesfazendo(true)
              }}
            >
              Desfazer alocação
            </Button>
          ) : null}
          {podeTrocar ? (
            <Button variant="outline" asChild>
              <Link to="/reservas/$reservaId/trocar-veiculo" params={{ reservaId: reserva.id }}>
                Trocar veículo…
              </Link>
            </Button>
          ) : null}
        </div>
        {reserva.status === 'RETIRADA_PENDENTE' && podeDesfazer ? (
          <p className="text-xs text-fg-muted">
            Desfazer agora cancela o contrato desta reserva (distrato, se já assinado).
          </p>
        ) : null}
      </CardContent>

      {aberto ? (
        <DialogoDeCandidatos
          reservaId={reserva.id}
          aoFechar={() => {
            setAberto(false)
          }}
        />
      ) : null}

      <Dialog
        open={desfazendo}
        onOpenChange={(abrir) => {
          if (!abrir) {
            setDesfazendo(false)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desfazer alocação</DialogTitle>
            <DialogDescription>
              A reserva volta para APROVADA, o bloqueio de agenda some e o carro volta à disputa. O
              motivo é obrigatório.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={motivo}
            onChange={(evento) => {
              setMotivo(evento.target.value)
            }}
            placeholder="Por que o carro sai desta reserva?"
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setDesfazendo(false)
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={motivo.trim().length < 5 || desfazer.isPending}
              onClick={() => {
                desfazer.mutate(
                  { motivo: motivo.trim() },
                  {
                    onSuccess: () => {
                      toast.success('Alocação desfeita — o carro voltou à disputa.')
                      setDesfazendo(false)
                      setMotivo('')
                    },
                    onError: (erro) => {
                      toast.error(errorMessage(erro))
                    },
                  },
                )
              }}
            >
              Desfazer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

/**
 * Os candidatos pontuados — "o score de cada candidato e o porquê" (prompt da
 * fase). Elegíveis com o breakdown por fator; bloqueados com os motivos, porque
 * "os três livres estão com a blindagem vencendo" é informação de despacho, não
 * um "indisponível" seco.
 */
function DialogoDeCandidatos({
  reservaId,
  aoFechar,
}: {
  readonly reservaId: string
  readonly aoFechar: () => void
}): ReactElement {
  const candidatos = useCandidatos(reservaId)
  const alocar = useAlocar(reservaId)
  const [manual, setManual] = useState<CandidatoLido | null>(null)
  const [justificativa, setJustificativa] = useState('')

  function aoAlocar(corpo: { vehicleId?: string; justificativa?: string }): void {
    alocar.mutate(corpo, {
      onSuccess: (feita) => {
        toast.success(
          feita.automatica
            ? `Algoritmo escolheu ${feita.placa} (score ${String(feita.score ?? 0)}).`
            : `${feita.placa} alocado por escolha manual.`,
        )
        aoFechar()
      },
      onError: (erro) => {
        toast.error(errorMessage(erro))
      },
    })
  }

  return (
    <Dialog
      open
      onOpenChange={(abrir) => {
        if (!abrir) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Candidatos à alocação</DialogTitle>
          <DialogDescription>
            Score determinístico, fator a fator. O override manual exige justificativa — e nem ela
            fura bloqueio duro (blindagem, documento, agenda).
          </DialogDescription>
        </DialogHeader>

        {candidatos.isPending ? <LoadingState rotulo="Pontuando os candidatos…" /> : null}
        {candidatos.isError ? (
          <ErrorState titulo="Não foi possível pontuar" erro={candidatos.error} />
        ) : null}

        {candidatos.data === undefined ? null : (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  aoAlocar({})
                }}
                disabled={alocar.isPending}
              >
                {alocar.isPending ? <LoaderCircle className="animate-spin" /> : null}
                Alocar pelo algoritmo
              </Button>
            </div>

            {candidatos.data.candidatos.map((candidato) => (
              <div
                key={candidato.vehicleId}
                className={`rounded-md border p-3 ${candidato.elegivel ? 'border-border' : 'border-danger-fg/30 opacity-80'}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {candidato.placa} · {candidato.modelo}
                    </span>
                    {candidato.elegivel ? (
                      <Badge variant="info">score {candidato.score}</Badge>
                    ) : (
                      <Badge variant="danger">bloqueado</Badge>
                    )}
                  </div>
                  {candidato.elegivel ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setManual(candidato)
                      }}
                    >
                      Escolher este
                    </Button>
                  ) : null}
                </div>

                {candidato.elegivel ? (
                  <div className="mt-2 grid gap-1 sm:grid-cols-2">
                    {candidato.contribuicoes.map((contribuicao) => (
                      <div
                        key={contribuicao.fator}
                        className="flex items-center justify-between gap-2 text-xs text-fg-secondary"
                      >
                        <span>{ROTULO_DO_FATOR[contribuicao.fator]}</span>
                        <span className="tabular-nums">
                          {contribuicao.nota} × {contribuicao.peso}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="mt-2 list-inside list-disc text-xs text-danger-fg">
                    {candidato.bloqueios.map((bloqueio) => (
                      <li key={bloqueio.motivo}>{bloqueio.detalhe}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Override manual: a justificativa é o que a auditoria lê depois. */}
        <Dialog
          open={manual !== null}
          onOpenChange={(abrir) => {
            if (!abrir) {
              setManual(null)
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Escolher {manual?.placa} à mão</DialogTitle>
              <DialogDescription>
                O algoritmo tinha outro candidato. Diga por que este — a justificativa é obrigatória
                e vai para a trilha.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={justificativa}
              onChange={(evento) => {
                setJustificativa(evento.target.value)
              }}
              placeholder="Ex.: cliente pediu a cor preta; o carro do algoritmo sai em translado…"
            />
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => {
                  setManual(null)
                }}
              >
                Cancelar
              </Button>
              <Button
                disabled={justificativa.trim().length < 5 || alocar.isPending}
                onClick={() => {
                  if (manual !== null) {
                    aoAlocar({ vehicleId: manual.vehicleId, justificativa: justificativa.trim() })
                  }
                }}
              >
                Alocar {manual?.placa}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Contratos
// ---------------------------------------------------------------------------

function CartaoDeContratos({ reserva }: { readonly reserva: ReservaDetalhe }): ReactElement {
  const contratos = useContratos(reserva.id)
  const gerar = useGerarContrato(reserva.id)
  const acao = useAcaoDeContrato(reserva.id)
  const baixar = useBaixarContrato()

  const podeGerar =
    temPermissao('contract.create') &&
    (reserva.status === 'VEICULO_ALOCADO' || reserva.status === 'RETIRADA_PENDENTE')

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSignature className="size-4" /> Contrato
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {contratos.isPending ? <LoadingState rotulo="Carregando contratos…" /> : null}
        {(contratos.data?.itens ?? []).length === 0 && !contratos.isPending ? (
          <EmptyState
            icone={FileText}
            titulo="Nenhum contrato gerado"
            descricao="Gere o contrato depois de alocar o veículo — o documento cita a placa designada, e a retirada só libera com ele assinado."
          />
        ) : null}

        {(contratos.data?.itens ?? []).map((contrato) => (
          <div key={contrato.id} className="rounded-md border border-border p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="font-medium">Contrato nº {contrato.numero}</span>
                <Badge
                  variant={
                    contrato.status === 'ASSINADO'
                      ? 'success'
                      : contrato.status === 'CANCELADO'
                        ? 'neutral'
                        : 'warning'
                  }
                >
                  {ROTULO_DO_STATUS_DE_CONTRATO[contrato.status]}
                </Badge>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    baixar.mutate(contrato.id, {
                      onError: (erro) => {
                        toast.error(errorMessage(erro))
                      },
                    })
                  }}
                >
                  Baixar PDF
                </Button>
                {contrato.status === 'GERADO' || contrato.status === 'ENVIADO_PARA_ASSINATURA' ? (
                  <Button
                    size="sm"
                    onClick={() => {
                      acao.mutate(
                        { contratoId: contrato.id, acao: 'assinar' },
                        {
                          onSuccess: () => {
                            toast.success('Assinatura registrada — a retirada pode ser liberada.')
                          },
                          onError: (erro) => {
                            toast.error(errorMessage(erro))
                          },
                        },
                      )
                    }}
                  >
                    Registrar assinatura
                  </Button>
                ) : null}
              </div>
            </div>
            <p className="mt-1 break-all text-xs text-fg-muted" title="SHA-256 do PDF gerado">
              hash {contrato.hashDoDocumento}
            </p>
          </div>
        ))}

        {podeGerar ? (
          <Button
            variant="outline"
            disabled={gerar.isPending}
            onClick={() => {
              gerar.mutate(undefined, {
                onSuccess: () => {
                  toast.success('Contrato gerado com o hash gravado.')
                },
                onError: (erro) => {
                  toast.error(errorMessage(erro))
                },
              })
            }}
          >
            {gerar.isPending ? <LoaderCircle className="animate-spin" /> : null}
            Gerar contrato
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Fechamento
// ---------------------------------------------------------------------------

interface AvariaOrcada {
  readonly descricao: string
  readonly valor: string
}

function CartaoDeFechamento({ reserva }: { readonly reserva: ReservaDetalhe }): ReactElement {
  const simular = useSimularFechamento(reserva.id)
  const fechar = useFechar(reserva.id)
  const [avarias, setAvarias] = useState<readonly AvariaOrcada[]>([])
  const [descricao, setDescricao] = useState('')
  const [valor, setValor] = useState('')
  const [previa, setPrevia] = useState<FechamentoLido | null>(null)
  const [multaIds, setMultaIds] = useState<readonly string[]>([])
  const [sinistroIds, setSinistroIds] = useState<readonly string[]>([])

  const podeFechar = temPermissao('settlement.create') && reserva.status === 'DEVOLUCAO_PENDENTE'
  const multas = useMultasDaReserva(podeFechar ? reserva.id : null)
  const sinistros = useSinistrosDaReserva(podeFechar ? reserva.id : null)

  if (reserva.fechamento !== null) {
    return <FechamentoGravado fechamento={reserva.fechamento} />
  }
  if (!podeFechar) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="size-4" /> Fechamento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-fg-secondary">
            O fechamento abre quando a devolução estiver em curso — depois das duas vistorias.
          </p>
          <Link
            to="/reservas/$reservaId/vistorias"
            params={{ reservaId: reserva.id }}
            className="mt-2 inline-block text-sm text-brand-700 underline-offset-4 hover:underline dark:text-brand-500"
          >
            Ir para as vistorias →
          </Link>
        </CardContent>
      </Card>
    )
  }

  const corpo = {
    avarias: [...avarias],
    multaIds: [...multaIds],
    sinistroIds: [...sinistroIds],
  }

  // Cobrança à mão fora da conta simulada não existe: mudou a seleção, a
  // prévia morreu — o botão de fechar volta a exigir uma simulação nova.
  function alternar(
    lista: readonly string[],
    definir: (valor: readonly string[]) => void,
    id: string,
  ): void {
    definir(lista.includes(id) ? lista.filter((item) => item !== id) : [...lista, id])
    setPrevia(null)
  }

  const multasCobraveis = (multas.data ?? []).filter(
    (multa) => multa.status === 'VINCULADA' || multa.status === 'CONDUTOR_INDICADO',
  )
  const sinistrosCobraveis = (sinistros.data ?? []).filter(
    (sinistro) =>
      sinistro.responsabilidadeDoCliente &&
      sinistro.franquiaDaApolice !== null &&
      sinistro.cobradoEm === null,
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardCheck className="size-4" /> Fechamento
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-fg-secondary">
          Atraso, km e combustível saem das VISTORIAS e do snapshot — nada é digitado. Aqui entram
          as avarias orçadas (o comparativo aponta as novas), as multas vinculadas e as franquias de
          sinistro com responsabilidade do cliente.
        </p>

        {multasCobraveis.length === 0 ? null : (
          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">
              Multas desta reserva
            </p>
            {multasCobraveis.map((multa) => (
              <label
                key={multa.id}
                className="flex cursor-pointer items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
              >
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="size-4 accent-brand-500"
                    checked={multaIds.includes(multa.id)}
                    onChange={() => {
                      alternar(multaIds, setMultaIds, multa.id)
                    }}
                  />
                  <span>
                    {multa.autoDeInfracao} — {multa.descricao}
                    <span className="ml-2 text-xs text-fg-muted">
                      {ROTULO_DO_STATUS_DE_MULTA[multa.status]}
                    </span>
                  </span>
                </span>
                <span className="tabular-nums">{formatarMoedaDoWire(multa.valor)}</span>
              </label>
            ))}
          </div>
        )}

        {sinistrosCobraveis.length === 0 ? null : (
          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">
              Sinistros com franquia a cobrar
            </p>
            {sinistrosCobraveis.map((sinistro) => (
              <label
                key={sinistro.id}
                className="flex cursor-pointer items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
              >
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="size-4 accent-brand-500"
                    checked={sinistroIds.includes(sinistro.id)}
                    onChange={() => {
                      alternar(sinistroIds, setSinistroIds, sinistro.id)
                    }}
                  />
                  <span>
                    {ROTULO_DO_TIPO_DE_SINISTRO[sinistro.tipo]} — {sinistro.descricao}
                  </span>
                </span>
                <span className="tabular-nums">
                  {sinistro.franquiaDaApolice === null
                    ? '—'
                    : formatarMoedaDoWire(sinistro.franquiaDaApolice)}
                </span>
              </label>
            ))}
            <p className="text-xs text-fg-muted">
              A franquia entra sob o mesmo teto de proteção das avarias — a conta simulada é a que
              vale.
            </p>
          </div>
        )}

        <div className="space-y-2">
          {avarias.map((avaria, indice) => (
            <div
              key={`${avaria.descricao}-${String(indice)}`}
              className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
            >
              <span>{avaria.descricao}</span>
              <span className="tabular-nums">{avaria.valor}</span>
            </div>
          ))}
          <div className="flex gap-2">
            <Input
              placeholder="Avaria orçada (descrição)"
              value={descricao}
              onChange={(evento) => {
                setDescricao(evento.target.value)
              }}
            />
            <Input
              placeholder="Valor (ex.: 300,00)"
              className="w-36"
              value={valor}
              onChange={(evento) => {
                setValor(evento.target.value)
              }}
            />
            <Button
              variant="outline"
              disabled={descricao.trim().length < 3 || valor.trim() === ''}
              onClick={() => {
                setAvarias([...avarias, { descricao: descricao.trim(), valor: valor.trim() }])
                setDescricao('')
                setValor('')
              }}
            >
              Incluir
            </Button>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={simular.isPending}
            onClick={() => {
              simular.mutate(corpo, {
                onSuccess: setPrevia,
                onError: (erro) => {
                  toast.error(errorMessage(erro))
                },
              })
            }}
          >
            {simular.isPending ? <LoaderCircle className="animate-spin" /> : null}
            Simular a conta
          </Button>
          <Button
            disabled={fechar.isPending || previa === null}
            title={previa === null ? 'Simule antes de fechar — confira a conta com o cliente.' : ''}
            onClick={() => {
              fechar.mutate(corpo, {
                onSuccess: () => {
                  toast.success('Locação fechada — o fechamento é imutável.')
                },
                onError: (erro) => {
                  toast.error(errorMessage(erro))
                },
              })
            }}
          >
            {fechar.isPending ? <LoaderCircle className="animate-spin" /> : null}
            Fechar a locação
          </Button>
        </div>

        {previa === null ? null : (
          <div className="rounded-md border border-border p-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-fg-muted">
              Prévia — nada foi gravado
            </p>
            <TabelaDeFechamento fechamento={previa} />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function FechamentoGravado({ fechamento }: { readonly fechamento: FechamentoLido }): ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardCheck className="size-4" /> Fechamento
          <Badge variant="success">Gravado</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <TabelaDeFechamento fechamento={fechamento} />
      </CardContent>
    </Card>
  )
}

/** A conta aberta, linha a linha — o espelho do breakdown da cotação. */
function TabelaDeFechamento({ fechamento }: { readonly fechamento: FechamentoLido }): ReactElement {
  return (
    <div className="space-y-2 text-sm">
      {fechamento.linhas.length === 0 ? (
        <p className="text-fg-secondary">Nada a cobrar — devolução em ordem.</p>
      ) : (
        fechamento.linhas.map((linha, indice) => (
          <div
            key={`${linha.tipo}-${String(indice)}`}
            className="flex items-baseline justify-between gap-4"
          >
            <span className="text-fg-secondary">{linha.descricao}</span>
            <span className="font-medium tabular-nums">{formatarMoedaDoWire(linha.valor)}</span>
          </div>
        ))
      )}
      <Separator />
      <div className="flex items-baseline justify-between gap-4 font-semibold">
        <span>Total de cobranças</span>
        <span className="tabular-nums">{formatarMoedaDoWire(fechamento.totalDeCobrancas)}</span>
      </div>
      <div className="flex items-baseline justify-between gap-4 text-fg-secondary">
        <span>Caução retida</span>
        <span className="tabular-nums">{formatarMoedaDoWire(fechamento.caucaoRetida)}</span>
      </div>
      <div className="flex items-baseline justify-between gap-4 text-fg-secondary">
        <span>Caução a liberar</span>
        <span className="tabular-nums">{formatarMoedaDoWire(fechamento.caucaoALiberar)}</span>
      </div>
      {fechamento.saldoACobrar === '0' ? null : (
        <div className="flex items-baseline justify-between gap-4 text-danger-fg">
          <span>Saldo a cobrar além da caução</span>
          <span className="tabular-nums">{formatarMoedaDoWire(fechamento.saldoACobrar)}</span>
        </div>
      )}
      {fechamento.creditoDeDevolucaoAntecipada === '0' ? null : (
        <div className="flex items-baseline justify-between gap-4 text-success-fg">
          <span>
            Crédito por devolução antecipada
            <span className="ml-1 text-xs text-fg-muted">
              (o que passar das cobranças é estornado no pagamento — Fase 5)
            </span>
          </span>
          <span className="tabular-nums">
            {formatarMoedaDoWire(fechamento.creditoDeDevolucaoAntecipada)}
          </span>
        </div>
      )}
      <p className="pt-1 text-xs text-fg-muted">
        {fechamento.diariasReais} diária(s) real(is) · {fechamento.diariasDeAtraso} de atraso ·{' '}
        {fechamento.kmRodado} km rodados ({fechamento.kmExcedente} além da franquia) ·{' '}
        {fechamento.oitavosFaltantes}/8 de tanque a repor
      </p>
      {fechamento.telemetria === null ? null : fechamento.telemetria.alerta ? (
        <div className="rounded-md border border-warning-fg/40 bg-warning/10 p-2 text-xs text-warning-fg">
          <p className="font-medium">Divergência com o rastreador</p>
          <p className="mt-0.5">
            O rastreador marca{' '}
            <span className="tabular-nums">{fechamento.telemetria.kmDoRastreador} km</span> —{' '}
            <span className="tabular-nums">
              {fechamento.telemetria.divergenciaKm > 0 ? '+' : ''}
              {fechamento.telemetria.divergenciaKm} km
            </span>{' '}
            em relação ao hodômetro da vistoria. Confira a digitação antes de fechar; a cobrança usa
            SEMPRE a vistoria.
          </p>
        </div>
      ) : (
        <p className="text-xs text-fg-muted">
          Rastreador confere:{' '}
          <span className="tabular-nums">{fechamento.telemetria.kmDoRastreador} km</span> (
          {fechamento.telemetria.divergenciaKm === 0
            ? 'sem divergência'
            : `${fechamento.telemetria.divergenciaKm > 0 ? '+' : ''}${String(fechamento.telemetria.divergenciaKm)} km`}
          ).
        </p>
      )}
    </div>
  )
}
